import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sprout, Users, Clock, UsersRound } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { CountryPicker, WalletCard, COUNTRY_KEY } from "./CountryWallet";

export const Landing = ({ settings, user, onSignIn, onStart, starting, resume, onResume, onPlayTogether }) => {
  const [nickname, setNickname] = useState("");
  const [country, setCountry] = useState(user?.country || localStorage.getItem(COUNTRY_KEY) || "UK");
  const [joinCode, setJoinCode] = useState("");
  const countries = settings?.countries || [];
  const startAmount = countries.find((c) => c.code === country)?.start_amount || 3000;

  useEffect(() => {
    if (user?.country) setCountry(user.country);
  }, [user?.country]);

  const pickCountry = (code) => {
    setCountry(code);
    localStorage.setItem(COUNTRY_KEY, code);
  };

  const submit = () => {
    if (!nickname.trim()) return;
    onStart(nickname.trim(), country, joinCode.trim().toUpperCase() || null);
  };

  return (
    <div className="bg-grid min-h-[calc(100vh-61px)] px-4 py-10 sm:py-16">
      <div className="max-w-5xl mx-auto">
        {resume && (
          <div
            data-testid="resume-banner"
            className="mb-6 rounded-2xl border border-yellow-500/40 bg-yellow-500/10 px-4 py-3 flex items-center gap-3"
          >
            <Clock className="w-5 h-5 text-yellow-400 shrink-0" />
            <p className="text-sm text-yellow-100 flex-1">
              You have a game in progress — <span className="font-semibold">{resume.nickname}</span>, Round{" "}
              {resume.current_round} of 8.
            </p>
            <Button
              data-testid="resume-game-btn"
              onClick={onResume}
              className="bg-yellow-500 hover:bg-yellow-400 text-slate-950 font-bold shrink-0 h-9"
            >
              Continue
            </Button>
          </div>
        )}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-400 border border-emerald-500/30 bg-emerald-500/10 rounded-full px-3 py-1">
            <Sprout className="w-3.5 h-3.5" /> Financial Literacy Simulation
          </span>
          <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-slate-50">
            Roll the dice.{" "}
            <span className="bg-gradient-to-r from-emerald-300 via-amber-300 to-pink-300 bg-clip-text text-transparent">
              Make money moves.
            </span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
            8 quick rounds of real-life money choices — saving, earning, investing, spending, life's
            curveballs and debt. Play it your way, then get a friendly AI coach's take on how you did.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="mt-10 rounded-3xl border border-slate-800 bg-slate-900/70 backdrop-blur p-6 sm:p-8 shadow-2xl"
        >
          <label className="text-xs font-semibold uppercase tracking-widest text-emerald-400">
            Your player nickname
          </label>
          <Input
            data-testid="nickname-input"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="Enter your player nickname..."
            className="mt-2 bg-slate-950/60 border-slate-700 text-slate-100 h-12 text-base placeholder:text-slate-600 focus-visible:ring-emerald-500"
          />

          <p className="mt-6 text-xs font-semibold uppercase tracking-widest text-emerald-400">
            Where do you live?
          </p>
          <p className="text-xs text-slate-500 mt-1">Scenarios and amounts match your country's money life.</p>
          <CountryPicker countries={countries} value={country} onChange={pickCountry} />
          <WalletCard user={user} country={country} startAmount={startAmount} onSignIn={onSignIn} />
          <p className="mt-3 text-[11px] text-slate-500">
            Your short questionnaire shapes how your starting money is split between cash, savings and debt.
          </p>

          <Button
            data-testid="start-session-btn"
            onClick={submit}
            disabled={!nickname.trim() || starting}
            className="mt-6 w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-slate-950 font-bold text-base rounded-xl shadow-lg shadow-emerald-500/25 disabled:opacity-50"
          >
            {starting ? "Starting…" : "Begin Simulation"}
            <ArrowRight className="w-5 h-5 ml-1" />
          </Button>

          <div className="mt-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-slate-500 shrink-0" />
            <Input
              data-testid="join-code-input"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              placeholder="Have a group code? Enter it (optional)"
              maxLength={6}
              className="bg-slate-950/60 border-slate-700 text-slate-100 h-10 text-sm font-mono tracking-widest placeholder:tracking-normal placeholder:font-body placeholder:text-slate-600 focus-visible:ring-emerald-500"
            />
          </div>
        </motion.div>

        <p className="mt-8 text-center text-xs text-slate-500 max-w-2xl mx-auto">
          A financial literacy simulation for anyone aged 16 and over.
        </p>

        <div className="mt-6 flex justify-center">
          <button
            data-testid="landing-play-together-btn"
            onClick={onPlayTogether}
            className="group inline-flex items-center gap-2.5 rounded-2xl border border-pink-500/30 bg-pink-500/[0.07] px-5 py-3 hover:bg-pink-500/[0.12] transition-colors"
          >
            <UsersRound className="w-5 h-5 text-pink-400" />
            <span className="text-sm text-slate-200">
              Playing with a partner or family? <span className="font-semibold text-pink-200">Play Together</span>
            </span>
            <ArrowRight className="w-4 h-4 text-pink-300 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
};
